import { Input } from '@tempo-io/sui-input';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseDialog } from '@/common/components/base-dialog/BaseDialog';
import { MULTI_TEXT_VALUE_MAX_LENGTH } from './validation';

interface TextEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValue?: string;
  onSave: (value: string) => void;
  mode: 'add' | 'edit';
}

export const TextEditDialog = ({
  open,
  onOpenChange,
  initialValue,
  onSave,
  mode,
}: TextEditDialogProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(initialValue ?? '');
  const [validationError, setValidationError] = useState<string>();

  useEffect(() => {
    if (open) {
      setValue(initialValue ?? '');
      setValidationError(undefined);
    }
  }, [open, initialValue]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) setValidationError(undefined);
    onOpenChange(newOpen);
  };

  const handleSave = () => {
    const trimmed = value.trim();
    if (!trimmed) {
      onOpenChange(false);
      return;
    }
    if (trimmed.length > MULTI_TEXT_VALUE_MAX_LENGTH) {
      setValidationError(
        t(($) => $.adaptivePlanner.itemPanel.customFields.validation.multiTextValueMaxLength, {
          maxLength: MULTI_TEXT_VALUE_MAX_LENGTH,
        }),
      );
      return;
    }
    onSave(trimmed);
    onOpenChange(false);
  };

  return (
    <BaseDialog
      open={open}
      onOpenChange={handleOpenChange}
      showCloseButton
      className="z-1400!"
      title={
        mode === 'add'
          ? t(($) => $.adaptivePlanner.itemPanel.textField.dialog.addTitle)
          : t(($) => $.adaptivePlanner.itemPanel.textField.dialog.editTitle)
      }
      primaryAction={{
        label: t(($) => $.common.save),
        onClick: handleSave,
        'data-testid': 'text-save-button',
      }}
      secondaryAction={{
        label: t(($) => $.common.cancel),
        onClick: () => onOpenChange(false),
        'data-testid': 'text-cancel-button',
      }}
    >
      <div className="flex flex-col gap-4 py-4">
        <div className="flex flex-col gap-2">
          <label htmlFor="text-value" className="text-sm font-medium">
            {t(($) => $.adaptivePlanner.itemPanel.textField.valueLabel)}
          </label>
          <Input
            id="text-value"
            data-testid="text-value-input"
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t(($) => $.adaptivePlanner.itemPanel.textField.valuePlaceholder)}
            autoFocus
          />
        </div>
        {validationError && (
          <span className="text-sm text-text-error" data-testid="text-validation-error">
            {validationError}
          </span>
        )}
      </div>
    </BaseDialog>
  );
};
